import { useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { Link, useNavigate } from "react-router-dom";
import AuthLayout from "./AuthLayout";
import { AuthField, AuthPasswordField, AuthSubmit, authLinkClass } from "./AuthField";
import { useScriptMode } from "../../contexts/ScriptModeContext";
import { useAuth } from "../../contexts/AuthContext";
import { useSignupMutation, useVerifyEmailCodeMutation, useSendVerificationCodeMutation } from "../../api/authApi";
import { useToast } from "../../components/ui/Toast";

/** Mobil uygulamayla aynı: 3 adım (kimlik → hesap → doğrulama), çubuk göstergesi. */
function Steps({ step, labels }) {
  return (
    <ol className="mb-6 flex items-center gap-3">
      {labels.map((label, i) => {
        const active = i === step;
        const done = i < step;
        return (
          <li key={label} className="flex flex-1 items-center gap-2">
            <span
              className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-colors duration-200 ${
                done || active
                  ? "bg-terakkiBrand text-white"
                  : "bg-terakkiBrandMist text-terakkiBrandDeep"
              }`}
            >
              {done ? "✓" : i + 1}
            </span>
            <span
              className={`truncate text-sm ${
                active ? "font-semibold text-terakkiInk" : "text-terakkiNeutral"
              }`}
            >
              {label}
            </span>
          </li>
        );
      })}
    </ol>
  );
}

export default function SignupPage() {
  const { t } = useScriptMode();
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  // step 0: isim/soyisim, 1: e-posta/şifre, 2: doğrulama kodu
  const [step, setStep] = useState(0);
  const [pendingEmail, setPendingEmail] = useState("");
  const [resendCountdown, setResendCountdown] = useState(0);

  const schema1 = yup.object({
    email: yup.string().email(t("validation_email")).required(t("validation_required")),
    password: yup.string().min(8, t("validation_password_min")).required(t("validation_required")),
    passwordConfirm: yup
      .string()
      .oneOf([yup.ref("password")], t("validation_password_match"))
      .required(t("validation_required")),
    name: yup.string().required(t("validation_required")),
    surname: yup.string().required(t("validation_required")),
  });

  const schema2 = yup.object({
    code: yup.string().length(6, t("validation_code_length")).required(t("validation_required")),
  });

  const form1 = useForm({ resolver: yupResolver(schema1), defaultValues: { email: "", password: "", passwordConfirm: "", name: "", surname: "" } });
  const form2 = useForm({ resolver: yupResolver(schema2), defaultValues: { code: "" } });

  const [signup, { isLoading: signupLoading }] = useSignupMutation();
  const [verifyEmailCode, { isLoading: verifyLoading }] = useVerifyEmailCodeMutation();
  const [sendVerificationCode] = useSendVerificationCodeMutation();

  // Adım 1 → 2: yalnızca isim/soyisim doğrulanır, sunucuya gidilmez.
  const continueToAccount = async () => {
    const ok = await form1.trigger(["name", "surname"]);
    if (ok) setStep(1);
  };

  const submitStep1 = async (values) => {
    const result = await signup(values);
    if (result.error) {
      toast.error(result.error.message || t("signup_error"));
      return;
    }
    setPendingEmail(values.email);
    setStep(2);
    startResendCountdown();
  };

  const submitStep2 = async (values) => {
    const result = await verifyEmailCode({ email: pendingEmail, verificationCode: values.code });
    if (result.error) {
      toast.error(result.error.message || t("verify_error"));
      return;
    }
    const data = result.data?.data;
    if (data?.token) {
      setSession({ token: data.token, user: data.user });
      navigate("/uygulama/profil", { replace: true });
    } else {
      toast.success(t("verify_success_login"));
      navigate("/giris", { replace: true });
    }
  };

  const startResendCountdown = () => {
    setResendCountdown(60);
    const interval = setInterval(() => {
      setResendCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleResend = async () => {
    if (resendCountdown > 0) return;
    const result = await sendVerificationCode({ email: pendingEmail });
    if (result.error) {
      toast.error(result.error.message || t("verify_code_send_error"));
      return;
    }
    toast.success(t("verify_code_sent"));
    startResendCountdown();
  };

  return (
    <AuthLayout
      title={t("signup_title")}
      footer={
        <p className="text-center">
          {t("signup_already_have_account")}{" "}
          <Link to="/giris" className={authLinkClass}>
            {t("signup_login_link")}
          </Link>
        </p>
      }
    >
      <Steps
        step={step}
        labels={[t("signup_step_who"), t("signup_step_account"), t("signup_step_verify")]}
      />

      {step === 0 && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            continueToAccount();
          }}
          className="flex flex-col gap-4"
        >
          <AuthField
            id="name"
            label={t("name")}
            autoComplete="given-name"
            autoFocus
            error={form1.formState.errors.name?.message}
            {...form1.register("name")}
          />
          <AuthField
            id="surname"
            label={t("surname")}
            autoComplete="family-name"
            error={form1.formState.errors.surname?.message}
            {...form1.register("surname")}
          />
          <AuthSubmit>{t("signup_continue")}</AuthSubmit>
        </form>
      )}

      {step === 1 && (
        <form onSubmit={form1.handleSubmit(submitStep1)} className="flex flex-col gap-4">
          <button
            type="button"
            onClick={() => setStep(0)}
            className={`self-start text-base ${authLinkClass}`}
          >
            ◀ {t("signup_back")}
          </button>
          <AuthField
            id="signup-email"
            label={t("email")}
            type="email"
            autoComplete="email"
            placeholder="ornek@mail.com"
            autoFocus
            disabled={signupLoading}
            error={form1.formState.errors.email?.message}
            {...form1.register("email")}
          />
          <AuthPasswordField
            id="signup-password"
            label={t("password")}
            autoComplete="new-password"
            placeholder="••••••••"
            disabled={signupLoading}
            error={form1.formState.errors.password?.message}
            showLabel={t("password_show")}
            hideLabel={t("password_hide")}
            {...form1.register("password")}
          />
          <AuthPasswordField
            id="signup-password-confirm"
            label={t("password_confirm")}
            autoComplete="new-password"
            placeholder="••••••••"
            disabled={signupLoading}
            error={form1.formState.errors.passwordConfirm?.message}
            showLabel={t("password_show")}
            hideLabel={t("password_hide")}
            {...form1.register("passwordConfirm")}
          />
          <AuthSubmit loading={signupLoading}>{t("signup_submit")}</AuthSubmit>
        </form>
      )}

      {step === 2 && (
        <form onSubmit={form2.handleSubmit(submitStep2)} className="flex flex-col gap-4">
          <p className="text-base text-terakkiNeutral">
            {t("verify_subtitle", { email: pendingEmail })}
          </p>
          <AuthField
            id="signup-code"
            label={t("verify_code")}
            maxLength={6}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="000000"
            className="text-center text-lg tracking-[0.6em]"
            disabled={verifyLoading}
            error={form2.formState.errors.code?.message}
            {...form2.register("code")}
          />
          <AuthSubmit loading={verifyLoading}>{t("verify_submit")}</AuthSubmit>
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCountdown > 0}
            className={`text-center text-base ${authLinkClass}`}
          >
            {resendCountdown > 0 ? t("verify_resend_in", { seconds: resendCountdown }) : t("verify_resend")}
          </button>
        </form>
      )}
    </AuthLayout>
  );
}
